/**
 * sibling-swap.js
 * Instant color switching for Stiletto "sibling" swatches (each color is a separate product).
 *
 * Clicking a color swatch normally navigates to that color's own product page. This intercepts
 * the click, fetches the sibling page, swaps ONLY the main product section
 * (`[data-section-type="product"][data-is-full-product="true"]`) in place, re-initialises the
 * theme's product JS against the new node, and updates the URL via pushState. Per-color URLs,
 * SEO, reviews and inventory are all preserved. On hover it pre-fetches the HTML and pre-loads
 * the hero image so the click feels instant.
 *
 * SCOPE: only the main PDP product section. Quick-view and collection swatches are left alone so
 * the theme's own handlers run (fixes a quick-view regression).
 *
 * DEFENSIVE: if anything required is missing or throws, it falls back to a normal navigation
 * (window.location.href) — it can never leave the page broken.
 */
(function () {
  'use strict';

  // Main PDP product section only (quick-view uses data-is-quick-view and no data-is-full-product).
  var MAIN_SECTION_SEL = '[data-section-type="product"][data-is-full-product="true"]';
  var SWATCH_SEL = 'a[data-sibling-swatch]';
  var CACHE_LIMIT = 12;
  var EAGER_MAX = 4; // eagerly preload all colors only when there aren't too many (bandwidth safety)

  // Never run inside the theme editor — Shopify re-renders sections itself there.
  if (window.Shopify && window.Shopify.designMode) return;

  try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (e) {}

  var conn = navigator.connection;
  var saveData = !!(conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '')));

  var docCache = new Map();     // url -> Promise<Document>
  var cacheOrder = [];          // for simple LRU eviction
  var imgPreloaded = Object.create(null);
  var swapping = false;
  var queuedUrl = null;         // latest color requested while a swap is in flight
  var pendingNav = null;        // a back/forward that arrived mid-swap, applied once the swap settles
  var warmAllScheduled = false; // avoids stacking idle callbacks

  function registry() {
    return window.Shopify && window.Shopify.theme && window.Shopify.theme.sections;
  }

  // Only handle swatches that belong to the main PDP section (not quick-view / collection cards).
  function mainSwatch(target) {
    var a = target && target.closest && target.closest(SWATCH_SEL);
    if (!a || !a.href) return null;
    var section = a.closest('[data-section-type="product"]');
    if (!section) return null;
    if (section.hasAttribute('data-is-quick-view')) return null;
    if (section.getAttribute('data-is-full-product') !== 'true') return null;
    return a;
  }

  function currentSection() {
    return document.querySelector(MAIN_SECTION_SEL);
  }

  // ---- Prefetch on hover -----------------------------------------------------

  function rememberCache(url, promise) {
    docCache.set(url, promise);
    cacheOrder.push(url);
    while (cacheOrder.length > CACHE_LIMIT) {
      docCache.delete(cacheOrder.shift());
    }
  }

  function fetchDoc(url) {
    if (docCache.has(url)) return docCache.get(url);
    var p = fetch(url, { credentials: 'include' })
      .then(function (res) {
        if (!res.ok) throw new Error('bad status ' + res.status);
        return res.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      })
      .catch(function (err) {
        docCache.delete(url); // allow a retry on the real click
        throw err;
      });
    rememberCache(url, p);
    return p;
  }

  function preloadHeroFrom(doc, priority) {
    try {
      var section = doc.querySelector(MAIN_SECTION_SEL) || doc.querySelector('[data-section-type="product"]');
      if (!section) return;
      var img = section.querySelector('img[fetchpriority="high"]') || section.querySelector('.product__media img');
      if (!img) return;
      var src = img.getAttribute('src');
      var srcset = img.getAttribute('srcset');
      var key = srcset || src;
      if (!key || imgPreloaded[key]) return;
      imgPreloaded[key] = true;
      var link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      if (src) link.href = src;
      if (srcset) link.setAttribute('imagesrcset', srcset);
      var sizes = img.getAttribute('sizes');
      if (sizes) link.setAttribute('imagesizes', sizes);
      // High priority when the user is hovering (imminent click); low for eager background warming.
      try { link.fetchPriority = priority || 'low'; } catch (e) {}
      document.head.appendChild(link);
    } catch (e) {}
  }

  function warm(url, priority) {
    if (!url || saveData) return;
    fetchDoc(url).then(function (doc) { preloadHeroFrom(doc, priority); }).catch(function () {});
  }

  function onIntent(e) {
    var a = mainSwatch(e.target);
    if (a) warm(a.href, 'high'); // hover = imminent, load its image at high priority
  }

  // Eagerly preload EVERY color's page + hero image shortly after load, so even the first
  // (un-hovered) click is instant. Runs at idle + low priority so it never slows the current page.
  function warmAll() {
    if (saveData) return;
    if (conn && /(^|-)3g$/.test(conn.effectiveType || '')) return; // eager preload only on fast connections
    var sec = currentSection();
    if (!sec) return;
    var swatches = sec.querySelectorAll(SWATCH_SEL);
    if (!swatches.length || swatches.length > EAGER_MAX) return; // skip huge color families
    Array.prototype.forEach.call(swatches, function (a) {
      if (!a.classList.contains('selected') && a.href) warm(a.href, 'low');
    });
  }

  function scheduleWarmAll() {
    if (warmAllScheduled) return;
    warmAllScheduled = true;
    var run = function () { warmAllScheduled = false; try { warmAll(); } catch (e) {} };
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2500 });
    else setTimeout(run, 1200);
  }

  // ---- Mount / unmount the product section ----------------------------------

  function unmountProduct(node) {
    var reg = registry();
    if (!reg || !reg.instances) return;
    for (var i = reg.instances.length - 1; i >= 0; i--) {
      var inst = reg.instances[i];
      if (inst && inst.container === node) {
        // The theme's Product.unload() forgets to tear this down -> resize-listener leak. Do it here.
        try {
          if (inst.product && inst.product.breakPointHandler && typeof inst.product.breakPointHandler.unload === 'function') {
            inst.product.breakPointHandler.unload();
          }
        } catch (e) {}
        try { if (typeof inst.onUnload === 'function') inst.onUnload(); } catch (e) {}
        reg.instances.splice(i, 1);
      }
    }
  }

  function mountProduct(node) {
    var reg = registry();
    if (!reg || !reg.registered || !reg.registered.product) throw new Error('no product section constructor');
    var inst = new reg.registered.product(node);
    reg.instances.push(inst);
    return inst;
  }

  // ---- Post-swap housekeeping -----------------------------------------------

  function visibleOptionSelects(root) {
    var out = [];
    var selects = root.querySelectorAll('select[name^="options"]');
    for (var i = 0; i < selects.length; i++) {
      if (!selects[i].closest('.product__option--hidden-sibling-duplicate')) out.push(selects[i]);
    }
    return out;
  }

  // Keep the shopper's chosen option values if the new color has them. The theme wires ProductForm
  // asynchronously (after a product fetch), so apply on a short retry rather than once.
  function preserveOptions(prevValues, toNode) {
    if (!prevValues.length) return;
    var attempts = 0;
    (function apply() {
      try {
        var selects = visibleOptionSelects(toNode);
        selects.forEach(function (sel, idx) {
          var want = prevValues[idx];
          if (want == null) return;
          var ok = Array.prototype.some.call(sel.options, function (o) { return o.value === want; });
          if (ok && sel.value !== want) {
            sel.value = want;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      } catch (e) {}
      if (++attempts < 6) setTimeout(apply, attempts * 80);
    })();
  }

  function reinitApps(node) {
    // Cloned <script>s don't execute, so app-injected blocks (reviews etc.) need a nudge.
    try { if (window.SPR && typeof window.SPR.initDomEls === 'function') { window.SPR.initDomEls(); window.SPR.loadProducts && window.SPR.loadProducts(); } } catch (e) {}
    // Generic hook for any other app / custom integration.
    try { document.dispatchEvent(new CustomEvent('sibling:swapped', { detail: { container: node } })); } catch (e) {}
  }

  function announce(node) {
    try {
      var sel = node.querySelector('a[data-sibling-swatch].selected');
      var label = sel && (sel.getAttribute('aria-label') || sel.getAttribute('data-sibling-cutline'));
      if (sel && typeof sel.focus === 'function') sel.focus({ preventScroll: true });
      var live = document.getElementById('sibling-swap-status');
      if (live && label) live.textContent = label;
    } catch (e) {}
  }

  function updateHeadMeta(doc, url) {
    try {
      if (doc.title) document.title = doc.title;
      copyMeta('link[rel="canonical"]', 'href', doc, url);
      copyMeta('meta[property="og:url"]', 'content', doc);
      copyMeta('meta[property="og:title"]', 'content', doc);
      copyMeta('meta[property="og:image"]', 'content', doc);
    } catch (e) {}
  }

  function copyMeta(selector, attr, doc, fallback) {
    var src = doc.querySelector(selector);
    var dest = document.querySelector(selector);
    var value = src ? src.getAttribute(attr) : fallback;
    if (dest && value != null) dest.setAttribute(attr, value);
  }

  // Fire ONE non-pageview signal. We deliberately do NOT emit a GA/Shopify "page_view" here to
  // avoid double-counting when Shopify Web Pixels / GA already track SPA navigations.
  function emitEvent(url) {
    try { if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: 'sibling_color_view', page_location: url }); } catch (e) {}
  }

  // ---- The swap ---------------------------------------------------------------

  function swap(url, push, currentNode) {
    var current = currentNode || currentSection();
    if (!current || !registry()) { window.location.href = url; return; }

    swapping = true;
    var scrollY = window.scrollY;
    var prevValues = visibleOptionSelects(current).map(function (s) { return s.value; });
    document.documentElement.classList.add('is-sibling-swapping');

    fetchDoc(url).then(function (doc) {
      var next = doc.querySelector(MAIN_SECTION_SEL) || doc.querySelector('[data-section-type="product"]');
      if (!next) throw new Error('no product section in response');

      var imported = document.importNode(next, true);
      unmountProduct(current);
      current.replaceWith(imported);
      mountProduct(imported); // full re-init scoped to the new node

      preserveOptions(prevValues, imported);
      reinitApps(imported);
      if (push !== false) history.pushState({ siblingSwap: true }, '', url);
      updateHeadMeta(doc, url);
      window.scrollTo(0, scrollY);
      announce(imported);
      emitEvent(url);
    }).catch(function () {
      window.location.href = url; // graceful fallback — never leave a broken page
    }).then(function () {
      document.documentElement.classList.remove('is-sibling-swapping');
      swapping = false;
      // A back/forward that arrived mid-swap -> finish with a clean full navigation.
      if (pendingNav) { var nav = pendingNav; pendingNav = null; window.location.href = nav; return; }
      // Run the most recent color the user asked for while we were busy.
      if (queuedUrl) {
        var next = queuedUrl;
        queuedUrl = null;
        if (next.split('#')[0] !== location.href.split('#')[0]) { swap(next, true); return; }
      }
      scheduleWarmAll(); // preload the (new) page's other colors too
    });
  }

  // ---- Wiring -----------------------------------------------------------------

  function onClick(e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = mainSwatch(e.target);
    if (!a) return; // not a main-PDP swatch -> let the theme's own handler run (quick view etc.)
    if (a.classList.contains('selected')) { e.preventDefault(); return; }
    e.preventDefault();
    e.stopImmediatePropagation(); // beat the theme's page-transition delegate
    if (swapping) { queuedUrl = a.href; return; } // don't drop the click
    swap(a.href, true, a.closest(MAIN_SECTION_SEL));
  }

  window.addEventListener('popstate', function () {
    var sec = currentSection();
    if (!sec) return; // not on a swapped PDP -> let the browser handle it
    // A swap is mid-flight: defer to a clean full navigation once it settles (avoids an overlap race).
    if (swapping) { pendingNav = location.href.split('#')[0]; return; }
    var handle = sec.getAttribute('data-product-handle');
    var path = location.pathname;
    // URL already matches what's on screen -> nothing to do.
    if (handle && path.indexOf('/products/' + handle) > -1) return;
    // Back/forward landed on a different product URL (incl. the original entry) -> resync the DOM.
    if (path.indexOf('/products/') > -1) {
      swap(location.href.split('#')[0], false);
    } else {
      window.location.reload(); // left the product entirely -> hard reload for correctness
    }
  });

  // A screen-reader live region for announcing the color change.
  try {
    if (!document.getElementById('sibling-swap-status')) {
      var live = document.createElement('div');
      live.id = 'sibling-swap-status';
      live.className = 'visually-hidden';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('role', 'status');
      (document.body || document.documentElement).appendChild(live);
    }
  } catch (e) {}

  // Delegated listeners (registry is only touched at interaction time, so no load-order race).
  document.addEventListener('click', onClick, true); // capture: runs before the theme's body delegate
  ['pointerover', 'touchstart', 'focusin'].forEach(function (ev) {
    document.addEventListener(ev, onIntent, { passive: true, capture: true });
  });

  // Eagerly preload all of this product's other colors in the background, so the first click is instant.
  if (document.readyState === 'complete') scheduleWarmAll();
  else window.addEventListener('load', scheduleWarmAll);
})();